import styled from "@emotion/styled";
import { type Task, createAndUpdateTask } from "@repo/api";
import {
  Icon,
  LightMode,
  Text,
  Variables,
  chevronRightSmallIcon,
} from "@repo/skapa";
import { t } from "@repo/translation";
import {
  ListViewItemWithActions,
  ListViewItemWithActionsCheckbox,
  Name,
  SimpleLabelField,
  getTaskIcon,
  useSettings,
  useToast,
} from "@repo/ui";
import { getErrorMessage } from "@repo/utils";

import { TaskTimeInfo } from "./TaskTimeInfo";
import { getTaskTitle } from "./utils";

const AddOnContainer = styled("span")`
  display: flex;

  & > :not(:first-child) {
    &::before {
      content: "|";
      margin: 0 ${Variables.space25};
    }
  }
`;

export const UpcomingTaskItem = ({
  editable,
  task,
  onClick,
  inset = true,
  showProjectInfo = false,
}: {
  editable?: boolean;
  task: Task;
  onClick?: () => void;
  inset?: boolean;
  showProjectInfo?: boolean;
}) => {
  const { updateToast } = useToast();
  const { task_configuration } = useSettings();

  const toggleTaskDone = () => {
    createAndUpdateTask({ ...task, isDone: !task.isDone })
      .then(() => {
        updateToast({
          id: task.id,
          title: t("prompt.task_saved"),
        });
      })
      .catch((err) => {
        updateToast({
          id: task.id,
          title: t("prompt.task_saved_failed"),
          message: getErrorMessage(err),
          type: "error",
        });
      });
  };

  return (
    <ListViewItemWithActions
      inset={inset}
      icon={getTaskIcon(
        task.parentTaskType,
        task.subTaskType,
        task_configuration?.task_types
      )}
      title={getTaskTitle(task)}
      description={
        <>
          <Text color={LightMode.colourTextAndIcon1} textStyle="Body.M">
            {task.description}
          </Text>
          <Text color={LightMode.colourTextAndIcon3} textStyle="Body.S">
            {task.orderId && (
              <SimpleLabelField
                small
                label={t("label.order_id")}
                value={task.orderId}
                color={LightMode.colourTextAndIcon2}
              />
            )}
          </Text>
        </>
      }
      addOn={
        <div>
          {showProjectInfo && (
            <AddOnContainer>
              {task.opportunityId && (
                <Text
                  color={LightMode.colourTextAndIcon3}
                  textStyle="Body.S"
                  title={t("label.project_id")}
                >
                  {task.opportunityId}
                </Text>
              )}

              {task.customerFirstName && task.customerLastName && (
                <Text color={LightMode.colourTextAndIcon3} textStyle="Body.S">
                  <Name
                    firstName={task.customerFirstName}
                    lastName={task.customerLastName}
                  />
                </Text>
              )}
            </AddOnContainer>
          )}
          <TaskTimeInfo task={task} />
        </div>
      }
      onClick={editable ? onClick : undefined}
      action={
        editable ? (
          <>
            <ListViewItemWithActionsCheckbox
              subtle
              label={getTaskTitle(task)}
              id={`checkbox-${task.id}`}
              value={task.id?.toString()}
              onChange={(e) => {
                e?.preventDefault();
                e?.stopPropagation();
                toggleTaskDone?.();
              }}
              checked={task.isDone}
            />
            <Icon paths={chevronRightSmallIcon} />
          </>
        ) : undefined
      }
    />
  );
};
